import { FootballEvent, FootballGame, GameStatistic, GameTimelineEvent } from '@/types';
import { sameFootballMatch } from '@/utils/teamMatching';

const API_BASE = process.env.EXPO_PUBLIC_API_BASE;
const BASE = API_BASE ? `${API_BASE}/scores365` : '';

const LANG_ID = 31;
const COUNTRY_ID = 21;
const TIMEZONE = 'America/Sao_Paulo';

export const SPORT_365 = {
  futebol: 1,
  basquete: 2,
  futebolAmericano: 6,
} as const;

type Game365 = {
  gameId: number;
  competition: string;
  homeName: string;
  awayName: string;
  homeLogo: string;
  awayLogo: string;
  homeScore: number;
  awayScore: number;
  status: FootballGame['status'];
  statusText: string;
  minute?: number;
  startTime: string;
};

type Event365 = {
  competitorId?: number | string;
  gameTime?: number | string;
  gameTimeDisplay?: string;
  order?: number | string;
  playerId?: number | string;
  extraPlayers?: Array<number | string>;
  eventType?: {
    id?: number;
    name?: string;
    subTypeName?: string;
  };
};

function params(extra: Record<string, string | number>): string {
  return new URLSearchParams({
    appTypeId: '5',
    langId: String(LANG_ID),
    timezoneName: TIMEZONE,
    userCountryId: String(COUNTRY_ID),
    ...Object.fromEntries(Object.entries(extra).map(([k, v]) => [k, String(v)])),
  }).toString();
}

function logoUrl(comp: any): string {
  if (!comp?.id) return '';
  const version = comp.imageVersion ?? 1;
  return (
    'https://imagecache.365scores.com/image/upload/' +
    'f_png,w_68,h_68,c_limit,q_auto:eco,dpr_2,d_Competitors:default.png/' +
    `v${version}/Competitors/${comp.id}`
  );
}

function score(comp: any): number {
  const value = comp?.score;
  return typeof value === 'number' && value >= 0 ? Math.round(value) : 0;
}

function statusFromGroup(group: number): FootballGame['status'] {
  if (group === 3) return 'live';
  if (group === 4) return 'finished';
  return 'scheduled';
}

function minuteFromDisplay(value: unknown): number | undefined {
  if (typeof value !== 'string') return undefined;
  const match = value.match(/\d+/);
  return match ? Number(match[0]) : undefined;
}

function sortOrderFromDisplay(value: unknown, fallback: number = 0): number {
  if (typeof value !== 'string') return fallback;
  const match = value.replace("'", '').trim().match(/^(\d+)(?:\+(\d+))?/);
  if (!match) return fallback;
  const minute = Number(match[1]);
  const added = match[2] ? Number(match[2]) / 100 : 0;
  return minute + added;
}

function mapGame(game: any): Game365 {
  const home = game?.homeCompetitor ?? {};
  const away = game?.awayCompetitor ?? {};
  return {
    gameId: game?.id,
    competition: game?.competitionDisplayName ?? '',
    homeName: home?.name ?? '',
    awayName: away?.name ?? '',
    homeLogo: logoUrl(home),
    awayLogo: logoUrl(away),
    homeScore: score(home),
    awayScore: score(away),
    status: statusFromGroup(game?.statusGroup),
    statusText: game?.statusText ?? game?.shortStatusText ?? '',
    minute: minuteFromDisplay(game?.gameTimeDisplay),
    startTime: game?.startTime ?? new Date().toISOString(),
  };
}

function toFootballGame(game: Game365): FootballGame {
  const mapped: FootballGame = {
    id: `365:${game.gameId}`,
    sport: 'futebol',
    league: {
      id: `365:${game.competition}`,
      name: game.competition || '365scores',
      logo: '',
      country: 'International',
      flag: '⚽',
    },
    homeTeam: {
      id: `365:${game.gameId}:home`,
      name: game.homeName,
      shortName: game.homeName,
      logo: game.homeLogo,
    },
    awayTeam: {
      id: `365:${game.gameId}:away`,
      name: game.awayName,
      shortName: game.awayName,
      logo: game.awayLogo,
    },
    score: {
      home: game.homeScore,
      away: game.awayScore,
    },
    status: game.status,
    minute: game.minute,
    startTime: game.startTime,
  };
  mapped.timeline = statusTimeline(mapped);
  return mapped;
}

function statusTimeline(game: FootballGame): GameTimelineEvent[] {
  const items: GameTimelineEvent[] = [];
  if (game.status !== 'scheduled') {
    items.push({ id: `${game.id}:start`, side: 'center', type: 'game_start', title: 'Início da partida' });
  }
  if (game.status === 'halftime' || game.status === 'finished' || (game.minute ?? 0) > 45) {
    items.push({ id: `${game.id}:ht`, side: 'center', type: 'halftime', title: 'Fim do primeiro tempo' });
  }
  if (game.status === 'live' && (game.minute ?? 0) > 45) {
    items.push({ id: `${game.id}:2h`, side: 'center', type: 'period_start', title: 'Começou o segundo tempo' });
  }
  if (game.status === 'finished') {
    items.push({ id: `${game.id}:ft`, side: 'center', type: 'game_end', title: 'Fim de jogo' });
  }
  return items;
}

function numericId(value: unknown): number | undefined {
  if (value == null || value === '') return undefined;
  const number = Number(value);
  return Number.isFinite(number) ? number : undefined;
}

function memberById(members: any[], id?: unknown): any | undefined {
  const numeric = numericId(id);
  if (numeric == null) return undefined;
  return members.find((item) => Number(item.id) === numeric || Number(item.athleteId) === numeric);
}

function memberName(members: any[], id?: unknown): string | undefined {
  const member = memberById(members, id);
  return member?.shortName ?? member?.name;
}

function memberCompetitorId(members: any[], id?: unknown): number | undefined {
  const member = memberById(members, id);
  return numericId(member?.competitorId ?? member?.competitor?.id);
}

function eventCompetitorId(event: Event365, members: any[]): number | undefined {
  return numericId(event.competitorId) ?? memberCompetitorId(members, event.playerId);
}

function eventTeamSide(
  event: Event365,
  homeCompetitorId: number,
  awayCompetitorId: number,
  members: any[]
): FootballEvent['team'] | null {
  const competitorId = eventCompetitorId(event, members);
  if (competitorId === homeCompetitorId) return 'home';
  if (competitorId === awayCompetitorId) return 'away';
  return null;
}

function map365Event(
  event: Event365,
  homeCompetitorId: number,
  awayCompetitorId: number,
  members: any[]
): FootballEvent | null {
  const name = String(event.eventType?.name ?? '').toLowerCase();
  const subtype = String(event.eventType?.subTypeName ?? '').toLowerCase();
  const player = memberName(members, event.playerId);
  const assist = memberName(members, event.extraPlayers?.[0]);
  const team = eventTeamSide(event, homeCompetitorId, awayCompetitorId, members);

  let type: FootballEvent['type'] | null = null;
  if (name.includes('gol') || name.includes('goal')) {
    type = subtype.includes('penalty') || subtype.includes('pênalti') ? 'penalty' : 'goal';
  } else if (name.includes('cartão vermelho') || name.includes('red')) {
    type = 'red_card';
  } else if (name.includes('cartão amarelo') || name.includes('yellow')) {
    type = 'yellow_card';
  } else if (name.includes('substitution') || name.includes('substituição')) {
    type = 'substitution';
  }

  if (!type || !team) return null;
  return {
    minute: numericId(event.gameTime) ?? minuteFromDisplay(event.gameTimeDisplay) ?? 0,
    type,
    team,
    player,
    assist,
    displayMinute: event.gameTimeDisplay,
    sortOrder: sortOrderFromDisplay(event.gameTimeDisplay, numericId(event.gameTime) ?? numericId(event.order) ?? 0),
  };
}

function footballEventToTimeline(gameId: string, event: FootballEvent, index: number): GameTimelineEvent {
  const title =
    event.type === 'goal' ? 'Gol'
    : event.type === 'penalty' ? 'Gol de pênalti'
    : event.type === 'own_goal' ? 'Gol contra'
    : event.type === 'yellow_card' ? 'Cartão amarelo'
    : event.type === 'red_card' ? 'Cartão vermelho'
    : 'Substituição';
  return {
    id: `${gameId}:event:${index}`,
    side: event.team,
    minute: event.displayMinute ?? `${event.minute}'`,
    type: event.type === 'goal' || event.type === 'penalty' || event.type === 'own_goal'
      ? 'goal'
      : event.type === 'substitution'
        ? 'substitution'
        : 'card',
    title,
    sortOrder: event.sortOrder ?? event.minute,
    detail: event.assist ? `${event.player ?? ''} · ${event.assist}` : event.player,
  };
}

function timelineOrder(item: GameTimelineEvent): number {
  if (typeof item.sortOrder === 'number') return item.sortOrder;
  if (item.type === 'game_start') return 0;
  if (item.type === 'halftime' || item.type === 'period_end') return 45.9;
  if (item.type === 'period_start') return 45.95;
  if (item.type === 'game_end') return 130;
  return sortOrderFromDisplay(item.minute ?? item.period, 999);
}

function scoreImpact(event: FootballEvent): 'home' | 'away' | null {
  if (event.type === 'goal' || event.type === 'penalty') return event.team;
  if (event.type === 'own_goal') return event.team === 'home' ? 'away' : 'home';
  return null;
}

function scoreAfter(events: FootballEvent[], maxOrder: number): { home: number; away: number } {
  const score = { home: 0, away: 0 };
  events.forEach((event) => {
    const order = event.sortOrder ?? event.minute;
    if (order > maxOrder) return;
    const side = scoreImpact(event);
    if (side) score[side] += 1;
  });
  return score;
}

function build365Timeline(game: FootballGame, events: FootballEvent[]): GameTimelineEvent[] {
  const runningScore = { home: 0, away: 0 };
  const eventItems = events.map((event, index) => {
    const item = footballEventToTimeline(game.id, event, index);
    const side = scoreImpact(event);
    if (side) {
      runningScore[side] += 1;
      return { ...item, score: { ...runningScore } };
    }
    return item;
  });
  const statusItems = statusTimeline(game).map((item) => {
    const order = timelineOrder(item);
    if (item.type === 'halftime' || item.type === 'period_start') {
      return { ...item, sortOrder: order, score: scoreAfter(events, 45.9) };
    }
    return { ...item, sortOrder: order };
  });

  return [...statusItems, ...eventItems].sort((a, b) => timelineOrder(b) - timelineOrder(a));
}

function statNumber(value: unknown): number | undefined {
  const parsed = Number.parseFloat(String(value ?? '').replace('%', '').replace(',', '.'));
  return Number.isFinite(parsed) ? parsed : undefined;
}

function map365Stats(game: any): GameStatistic[] | undefined {
  const stats = game?.statistics ?? game?.stats ?? game?.gameStatistics ?? [];
  if (!Array.isArray(stats) || stats.length === 0) return undefined;
  const rows: GameStatistic[] = [];
  stats.forEach((item: any, index: number) => {
    const label = item.name ?? item.title ?? item.typeName;
    const home = item.home ?? item.homeValue ?? item.homeCompetitorValue;
    const away = item.away ?? item.awayValue ?? item.awayCompetitorValue;
    if (!label || (home == null && away == null)) return;
    rows.push({
      id: String(item.id ?? label ?? index),
      label: String(label),
      home: String(home ?? '-'),
      away: String(away ?? '-'),
      homeValue: statNumber(home),
      awayValue: statNumber(away),
    });
  });
  return rows.length > 0 ? rows : undefined;
}

function map365StatsResponse(json: any, homeCompetitorId: number, awayCompetitorId: number): GameStatistic[] | undefined {
  const stats = json?.statistics ?? [];
  if (!Array.isArray(stats) || stats.length === 0) return undefined;

  const byStat = new Map<string, {
    id: string;
    label: string;
    order: number;
    home?: string;
    away?: string;
    homeValue?: number;
    awayValue?: number;
  }>();

  stats.forEach((item: any) => {
    const label = item.name ?? item.title ?? item.typeName;
    if (!label) return;
    const key = `${item.categoryId ?? 'main'}:${item.id ?? label}:${label}`;
    const current = byStat.get(key) ?? {
      id: key,
      label: String(label),
      order: Number(item.order ?? item.categoryOrder ?? 999),
    };
    const value = String(item.value ?? '-');
    const numeric = statNumber(item.valuePercentage ?? item.value);
    const competitorId = Number(item.competitorId);
    if (competitorId === homeCompetitorId) {
      current.home = value;
      current.homeValue = numeric;
    }
    if (competitorId === awayCompetitorId) {
      current.away = value;
      current.awayValue = numeric;
    }
    byStat.set(key, current);
  });

  const rows = Array.from(byStat.values())
    .filter((item) => item.home != null || item.away != null)
    .sort((a, b) => a.order - b.order)
    .map((item) => ({
      id: item.id,
      label: item.label,
      home: item.home ?? '-',
      away: item.away ?? '-',
      homeValue: item.homeValue,
      awayValue: item.awayValue,
    }));

  return rows.length > 0 ? rows : undefined;
}

async function get365FootballStats(rawId: string, homeCompetitorId: number, awayCompetitorId: number): Promise<GameStatistic[] | undefined> {
  try {
    const url = `${BASE}/game/stats/?${params({ games: rawId })}`;
    const res = await fetch(url);
    if (!res.ok) return undefined;
    const json = await res.json();
    return map365StatsResponse(json, homeCompetitorId, awayCompetitorId);
  } catch {
    return undefined;
  }
}

export async function get365FootballGames(): Promise<FootballGame[]> {
  if (!BASE) return [];

  try {
    const url = `${BASE}/games/current/?${params({ sports: SPORT_365.futebol })}`;
    const res = await fetch(url);
    if (!res.ok) return [];

    const json = await res.json();
    const games = (json?.games ?? [])
      .map(mapGame)
      .filter((game: Game365) => game.gameId && game.homeName && game.awayName)
      .map(toFootballGame);

    console.log(`[365scores] Futebol: ${games.length} partidas`);
    return games;
  } catch (error) {
    console.warn('[365scores] Falha ao buscar futebol', error);
    return [];
  }
}

export async function get365FootballGamesList(): Promise<FootballGame[]> {
  if (!BASE) return [];

  try {
    const url = `${BASE}/games/?${params({ sports: SPORT_365.futebol })}`;
    const res = await fetch(url);
    if (!res.ok) return [];

    const json = await res.json();
    const games = (json?.games ?? [])
      .map(mapGame)
      .filter((game: Game365) => game.gameId && game.homeName && game.awayName)
      .map(toFootballGame);

    console.log(`[365scores] Lista futebol: ${games.length} partidas`);
    return games;
  } catch (error) {
    console.warn('[365scores] Falha ao buscar lista de futebol', error);
    return [];
  }
}

export async function get365FootballGameDetail(gameId: string): Promise<FootballGame | null> {
  if (!BASE) return null;

  const rawId = gameId.replace(/^365:/, '');
  try {
    const url = `${BASE}/game/?${params({ gameId: rawId })}`;
    const res = await fetch(url);
    if (!res.ok) return null;
    const json = await res.json();
    const game = json?.game;
    if (!game?.id) return null;

    const baseGame = toFootballGame(mapGame(game));
    const homeId = Number(game.homeCompetitor?.id);
    const awayId = Number(game.awayCompetitor?.id);
    const events = (game.events ?? [])
      .map((event: Event365) => map365Event(event, homeId, awayId, game.members ?? []))
      .filter((event: FootballEvent | null): event is FootballEvent => event !== null)
      .sort((a: FootballEvent, b: FootballEvent) => (a.sortOrder ?? a.minute) - (b.sortOrder ?? b.minute));

    baseGame.events = events;
    baseGame.timeline = build365Timeline(baseGame, events);
    baseGame.statistics = await get365FootballStats(rawId, homeId, awayId) ?? map365Stats(game);
    return baseGame;
  } catch {
    return null;
  }
}

function detailPriority(game: FootballGame): number {
  if (game.status === 'live' || game.status === 'halftime') return 0;
  if (game.status === 'finished') return 1;
  return 2;
}

export async function find365FootballGameDetailForMatch(match: FootballGame): Promise<FootballGame | null> {
  if (match.id.startsWith('365:')) return get365FootballGameDetail(match.id);

  const games = await get365FootballGamesList();
  const targetTime = Date.parse(match.startTime);
  const candidates = games
    .filter((game) => sameFootballMatch(game, match))
    .filter((game) => {
      if (!Number.isFinite(targetTime)) return true;
      const candidateTime = Date.parse(game.startTime);
      if (!Number.isFinite(candidateTime)) return true;
      return Math.abs(candidateTime - targetTime) <= 36 * 60 * 60 * 1000;
    })
    .sort((a, b) => {
      const statusDiff = detailPriority(a) - detailPriority(b);
      if (statusDiff !== 0) return statusDiff;
      if (!Number.isFinite(targetTime)) return 0;
      return Math.abs(Date.parse(a.startTime) - targetTime) - Math.abs(Date.parse(b.startTime) - targetTime);
    });

  return candidates[0] ? get365FootballGameDetail(candidates[0].id) : null;
}
